import React from 'react';
import './OnboardingStepper.scss';
import { Button, Stack, Typography } from '@mui/material';
import { ReactComponent as SupportIcon } from '../../../assets/onboarding/support.svg';
import {
  OnboardingStep,
  onboardingSteps,
  onboardingStepBarSections,
  onboardingStepBarTitles,
  onboardingStepBarDescriptions,
} from '../../../data-access';
import { ReactComponent as StepChecked } from '../../../assets/onboarding/step_checked.svg';
import { ReactComponent as StepUnchecked } from '../../../assets/onboarding/step_unchecked.svg';
import clsx from 'clsx';
import { groupBy } from 'lodash';
interface OnboardingStepperProps {
  currentStep?: OnboardingStep;
}

const OnboardingStepper: React.FC<OnboardingStepperProps> = ({ currentStep }) => {
  const stepsBySection = groupBy(
    onboardingSteps.filter((step) => step.stepBarKey),
    'stepBarKey'
  );

  const isAtFinalStep = currentStep && currentStep.order === onboardingSteps.length;

  return (
    <div className="onboarding-stepper">
      <div className="onboarding-stepper__container">
        <Stack gap={1} className="onboarding-stepper__steps">
          {onboardingStepBarSections.map((sectionKey, index) => {
            const sectionSteps = stepsBySection[sectionKey] || [];
            const currentSectionIndex = onboardingStepBarSections.findIndex(
              (s) => s === currentStep?.stepBarKey
            );
            const thisSectionIndex = onboardingStepBarSections.findIndex((s) => s === sectionKey);

            // A section is past if:
            // 1. Current step is in a later section, OR
            // 2. We're at the final step (all sections completed)
            const isPastSection = currentSectionIndex > thisSectionIndex || isAtFinalStep;

            // A section is future if current step is in an earlier section and not at final step
            const isFutureSection = currentSectionIndex < thisSectionIndex && !isAtFinalStep;
            const isLastSection = index === onboardingStepBarSections.length - 1;

            if (sectionSteps.length === 0) return null;

            return (
              <Stack
                key={sectionKey}
                className={clsx('onboarding-stepper__step', {
                  'onboarding-stepper__step--upcoming': isFutureSection,
                })}
                gap={1}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  {isPastSection ? (
                    <StepChecked className="onboarding-stepper__step-icon" />
                  ) : (
                    <StepUnchecked className="onboarding-stepper__step-icon" />
                  )}
                  <Typography variant="body1" className="onboarding-stepper__step-title">
                    {onboardingStepBarTitles[sectionKey]}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="flex-start" gap={1}>
                  <div className="onboarding-stepper__step-line-container">
                    <div
                      className={clsx('onboarding-stepper__step-line', {
                        'onboarding-stepper__step-line--hidden': isLastSection,
                      })}
                    />
                  </div>
                  <Typography variant="subtitle2" className="onboarding-stepper__step-description">
                    {onboardingStepBarDescriptions[sectionKey]}
                  </Typography>
                </Stack>
              </Stack>
            );
          })}
        </Stack>

        <Stack
          gap={2}
          mt="auto"
          position={'sticky'}
          bottom={0}
          className="onboarding-stepper__support-container"
        >
          <SupportIcon className="onboarding-stepper__support-icon" />
          <Stack gap={0.5}>
            <Typography className="onboarding-stepper__support-text" variant="body1">
              Having trouble?
            </Typography>
            <Typography
              className="onboarding-stepper__support-text-description"
              variant="subtitle2"
            >
              Feel free to contact us and we will always help you through the process.
            </Typography>
          </Stack>
          <Button
            className="onboarding-stepper__support-button"
            component={'a'}
            href="mailto:contact@demind-inc.com"
          >
            <Typography variant="body1" className="onboarding-stepper__support-button-text">
              Contact
            </Typography>
          </Button>
        </Stack>
      </div>
    </div>
  );
};

export default OnboardingStepper;
